import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PanelLeftOpen, PanelLeftClose, BookOpen, FileText, Loader2, ArrowLeft, Download, RefreshCw, Languages } from 'lucide-react';

import Button from '../../System Design/Button';
import api from '../../services/api';
import authService from '../../services/authService';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/DocumentViewer/Sidebar';
import ContentDisplay from '../../components/DocumentViewer/ContentDisplay';
import FullBookContent from '../../components/DocumentViewer/FullBookContent';
import ImageLightbox from '../../components/ImageLightbox';
import './DocumentViewer.css';
import { translateWithLanguage } from '../../i18n/i18n';

const PROCESSING_POLL_INTERVAL_MS = 2000;
const ACTIVE_PROCESSING_STATUSES = new Set(['queued', 'processing']);

const processingSnapshot = (data) => ({
  status: data?.status || null,
  progress: Number.isFinite(data?.progress) ? data.progress : null,
});

const processingSnapshotChanged = (previous, current) =>
  !previous ||
  previous.status !== current.status ||
  previous.progress !== current.progress;

const DocumentViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '', caption: '' });
  const [viewMode, setViewMode] = useState('full'); // 'simple' ou 'full' - démarrer en vue complète
  const [selectedItem, setSelectedItem] = useState(null);
  const [navigationRequestId, setNavigationRequestId] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [selectedLang, setSelectedLang] = useState('source');
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateMessage, setTranslateMessage] = useState(null);

  const handleTranslateBook = async () => {
    try {
      setTranslateMessage(null);
      setTranslateLoading(true);
      const originalLang = bookData?.book?.language || 'fr';
      const supportedTargets = ['fr', 'en', 'pt'];
      const targetLangs = supportedTargets.filter((lang) => lang !== originalLang);

      await api.post(`/books/${id}/finalize/`, {
        target_langs: targetLangs,
      });
      setTranslateMessage("Traduction lancée. Le contenu sera disponible progressivement en anglais et portugais.");
    } catch (error) {
      console.error('Erreur lors du lancement de la traduction du livre:', error);
      const msg = error?.response?.data?.error || error?.message || 'Erreur lors du lancement de la traduction';
      setTranslateMessage(msg);
    } finally {
      setTranslateLoading(false);
    }
  };

  const reloadBookData = useCallback(async (
    lang = selectedLang,
    isCurrent = () => true
  ) => {
    if (!id) return;
    try {
      const params = {};
      if (lang && lang !== 'source') {
        params.lang = lang;
      }
      const structure = await api.get(`/books/${id}/export_structure/`, { params });
      if (!isCurrent()) {
        return null;
      }
      setBookData(structure.data);

      // Si rien n'est sélectionné, sélectionner automatiquement le premier chapitre
      setSelectedItem((currentSelection) => {
        if (currentSelection || !structure.data?.chapters?.length) {
          return currentSelection;
        }
        return {
          type: 'chapter',
          data: structure.data.chapters[0],
          index: 0,
        };
      });

      return structure.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des données du livre:', error);
      return null;
    }
  }, [id, selectedLang]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    let lastProcessingSnapshot = null;

    const scheduleNextPoll = () => {
      if (!cancelled) {
        pollTimer = setTimeout(checkProcessingStatus, PROCESSING_POLL_INTERVAL_MS);
      }
    };

    const checkProcessingStatus = async () => {
      try {
        const response = await api.get(`/books/${id}/processing-status/`);
        if (cancelled) return;

        const currentSnapshot = processingSnapshot(response.data);
        const hasChanged = processingSnapshotChanged(
          lastProcessingSnapshot,
          currentSnapshot
        );
        let structureRefreshed = true;

        if (hasChanged) {
          structureRefreshed = Boolean(
            await reloadBookData(selectedLang, () => !cancelled)
          );
          if (cancelled) return;
          if (structureRefreshed) {
            lastProcessingSnapshot = currentSnapshot;
          }
        }

        if (
          ACTIVE_PROCESSING_STATUSES.has(currentSnapshot.status) ||
          !structureRefreshed
        ) {
          scheduleNextPoll();
        }
      } catch (error) {
        if (cancelled) return;
        console.warn(
          'Impossible de récupérer le statut du traitement, nouvelle tentative prévue:',
          error
        );
        scheduleNextPoll();
      }
    };

    const initializeBook = async () => {
      setLoading(true);
      setError(null);

      let initialSnapshot = null;
      try {
        try {
          const response = await api.get(`/books/${id}/processing-status/`);
          if (cancelled) return;
          initialSnapshot = processingSnapshot(response.data);
        } catch (statusError) {
          if (cancelled) return;
          console.warn(
            'Statut du traitement indisponible au chargement, le document reste accessible:',
            statusError
          );
        }

        const data = await reloadBookData(selectedLang, () => !cancelled);
        if (cancelled) return;

        if (!data) {
          setError('Impossible de charger les données du livre');
        } else {
          lastProcessingSnapshot = initialSnapshot;
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      if (
        !cancelled &&
        (
          !lastProcessingSnapshot ||
          ACTIVE_PROCESSING_STATUSES.has(lastProcessingSnapshot.status)
        )
      ) {
        scheduleNextPoll();
      }
    };

    if (id) {
      initializeBook();
    }

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
    };
  }, [id, selectedLang, reloadBookData]);

  const handleSelectContent = (content) => {
    // Passer automatiquement en vue complète lors de la sélection
    setViewMode('full');
    setSelectedItem(content);
    setNavigationRequestId((current) => current + 1);

    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
    
    // Si c'est la couverture, faire défiler vers l'image
    if (content.type === 'cover') {
      setTimeout(() => {
        const coverImage = document.getElementById('cover-image');
        if (coverImage) {
          coverImage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Regénérer le QCM pour un chapitre donné (utilisé par FullBookContent)
  const regenerateQCMForChapter = async (chapterId, language = null) => {
    try {
      setRegenError(null);
      if (!chapterId) return;
      setRegenLoading(true);
      
      // Préparer le payload avec la langue si fournie
      const payload = { chapter_id: chapterId, all_languages: true };
      if (language) {
        payload.language = language;
      }
      
      await api.post('/qcms/regenerate-chapter/', payload);
      // Recharger la structure (en conservant la langue courante)
      const data = await reloadBookData(selectedLang);
      // Conserver la sélection courante sur le chapitre régénéré
      const updatedChapter = data?.chapters?.find(c => c.id === chapterId);
      if (updatedChapter) {
        setSelectedItem({ type: 'chapter', data: updatedChapter, index: 0 });
      }
    } catch (e) {
      console.error('Erreur lors de la régénération du QCM (par chapitre):', e);
      setRegenError(e?.response?.data?.error || e?.message || 'Erreur lors de la régénération du QCM');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleRegenerateQCM = async () => {
    try {
      setRegenError(null);
      if (!selectedItem || selectedItem.type !== 'chapter') {
        return;
      }
      setRegenLoading(true);
      const chapterId = selectedItem.data?.id;
      if (!chapterId) return;
      await api.post('/qcms/regenerate-chapter/', { chapter_id: chapterId, all_languages: true });
      // Recharger la structure (en conservant la langue courante)
      const data = await reloadBookData(selectedLang);
      // Conserver la sélection courante avec les données à jour
      const updatedChapter = data?.chapters?.find(c => c.id === chapterId);
      if (updatedChapter) {
        setSelectedItem({ type: 'chapter', data: updatedChapter, index: selectedItem.index });
      }
    } catch (e) {
      console.error('Erreur lors de la régénération du QCM:', e);
      setRegenError(e?.response?.data?.error || e?.message || 'Erreur lors de la régénération du QCM');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      if (!id) return;

      const params = {};
      if (selectedLang && selectedLang !== 'source') {
        params.lang = selectedLang;
      }

      const res = await api.get(`/books/${id}/pdf/`, {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const rawTitle = bookData?.book?.title || `book-${id}`;
      const safeTitle = String(rawTitle).replace(/[\\/:*?"<>|]+/g, '_');
      const suffix = (selectedLang && selectedLang !== 'source') ? selectedLang : 'source';
      const filename = `${safeTitle}_${suffix}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
    }
  };

  const handleExportJson = () => {
    try {
      if (!bookData) {
        return;
      }

      const rawTitle = bookData.book?.title || `book-${id}`;
      const safeTitle = String(rawTitle).replace(/[\\/:*?"<>|]+/g, '_');
      const jsonString = JSON.stringify(bookData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors de l'export JSON du livre:", error);
    }
  };

  if (loading) {
    return (
      <div className="w-full flex flex-col bg-gray-50 min-h-screen items-center justify-center">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-gray-600 mt-2">Chargement du document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex flex-col bg-gray-50 min-h-screen items-center justify-center">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <FileText className="h-16 w-16" />
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <Button onClick={() => navigate('/documents')}>
            Retour aux documents
          </Button>
        </div>
      </div>
    );
  }

  const baseBookLanguage = bookData?.book?.language || 'fr';
  const contentLanguage = selectedLang === 'source' ? baseBookLanguage : selectedLang;
  const summaryLabel = translateWithLanguage(contentLanguage, 'documentViewer.summary');
  const hideSummaryLabel = translateWithLanguage(contentLanguage, 'documentViewer.hideSummary');
  const showSummaryLabel = translateWithLanguage(contentLanguage, 'documentViewer.showSummary');
  const closeSummaryLabel = translateWithLanguage(contentLanguage, 'documentViewer.closeSummary');
  const welcomeTitle = translateWithLanguage(contentLanguage, 'documentViewer.welcomeTitle');
  const welcomeText = translateWithLanguage(contentLanguage, 'documentViewer.welcomeText');
  const welcomeHint = translateWithLanguage(contentLanguage, 'documentViewer.welcomeHint');

  return (
    <div className="w-full flex flex-col bg-gray-50 min-h-screen">
      <ImageLightbox
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        caption={lightbox.caption}
        onClose={() => setLightbox({ open: false, src: '', alt: '', caption: '' })}
      />
      {/* Header - Fixe lors du défilement */}
      <div className="bg-white border-b border-gray-200 px-0 sm:px-5 py-0.5 sm:py-4 shadow-sm z-100 w-full sticky top-10 sm:top-0 overflow-hidden mobile-header-container">
        {/* Desktop Header */}
        <div className="hidden sm:flex items-center gap-1 sm:gap-5 w-full min-w-0">
          <Button
            variant="outline"
            onClick={() => navigate('/documents')}
            className="flex items-center gap-0.5 sm:gap-2 px-1.5 sm:px-4 py-1.5 sm:py-2 border border-gray-300 bg-white rounded-md cursor-pointer transition-all duration-200 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 flex-shrink-0"
          >
            <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Retour</span>
          </Button>

          {bookData?.book && (
            <div className="grid grid-cols-12 items-center flex-1 min-w-0 gap-0.5 sm:gap-2">
              {/* 9 colonnes pour le texte */}
              <div className="col-span-9 min-w-0 flex flex-col gap-0">
                <h1 className="text-sm sm:text-xl font-semibold text-gray-900 leading-tight truncate">
                  {bookData.book.title}
                </h1>
                <p className="text-xs text-gray-600 leading-tight">
                  Créé le {new Date(bookData.book.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* 1 colonne vide comme séparateur */}
              <div className="col-span-1" />

              {/* 2 colonnes pour la sélection de langue + Sommaire */}
              <div className="col-span-2 flex items-center justify-end gap-0.5 sm:gap-2 flex-shrink-0">
                <button
                  onClick={handleDownloadDocument}
                  className="inline-flex items-center gap-1 border border-gray-300 text-gray-700 bg-white rounded-md px-2 py-1 text-xs sm:text-sm hover:bg-gray-50"
                  title="Télécharger le livre en PDF (langue affichée)"
                >
                  <Download size={14} />
                  <span>PDF</span>
                </button>
                {isAdmin && (
                  <>
                    <button
                      onClick={handleExportJson}
                      className="inline-flex items-center gap-1 border border-gray-300 text-gray-700 bg-white rounded-md px-2 py-1 text-xs sm:text-sm hover:bg-gray-50"
                      title="Exporter la structure du livre en JSON"
                    >
                      <Download size={14} />
                      <span>JSON</span>
                    </button>
                    <button
                      onClick={handleTranslateBook}
                      disabled={translateLoading}
                      className="hidden lg:inline-flex items-center gap-1 border border-blue-300 text-blue-800 bg-blue-50 rounded-md px-2 py-1 text-xs sm:text-sm hover:bg-blue-100 disabled:opacity-60"
                      title="Lancer la traduction automatique (EN/PT)"
                    >
                      <Languages size={14} />
                      <span>{translateLoading ? 'Traduction...' : 'Traduire le livre'}</span>
                    </button>
                  </>
                )}
                {bookData?.book && (
                  <select
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                    className="border border-gray-300 rounded-md px-1.5 py-1 text-xs sm:text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Langue d'affichage du contenu"
                  >
                    <option value="source">Original ({bookData.book.language || 'fr'})</option>
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="pt">Português</option>
                  </select>
                )}
                <button
                  type="button"
                  aria-expanded={sidebarOpen}
                  aria-controls="book-table-of-contents"
                  onClick={toggleSidebar}
                  className="inline-flex items-center gap-1 bg-primary text-white border-none rounded-md px-2 py-1 sm:py-2 cursor-pointer transition-all duration-300 hover:bg-primary hover:-translate-y-0.5"
                  title={sidebarOpen ? hideSummaryLabel : showSummaryLabel}
                >
                  {sidebarOpen ? <PanelLeftClose size={16} className="sm:w-5 sm:h-5" /> : <PanelLeftOpen size={16} className="sm:w-5 sm:h-5" />}
                  <span>{summaryLabel}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Header - Titre uniquement */}
        <div className="sm:hidden flex items-center w-full px-1">
          {bookData?.book && (
            <div className="flex-1 min-w-0 pr-28">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {bookData.book.title}
              </h1>
              <p className="text-xs text-gray-600 leading-tight">
                {bookData.chapters?.length || 0} chapitre{bookData.chapters?.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
          <div className="flex items-center gap-1 flex-shrink-0 absolute right-0 top-0 pr-1 pt-1 sticky">
            {bookData?.book && (
              <button
                type="button"
                aria-expanded={sidebarOpen}
                aria-controls="book-table-of-contents-mobile"
                onClick={toggleSidebar}
                className="inline-flex items-center gap-1 bg-primary text-white border-none rounded-md px-2 py-1.5 text-xs cursor-pointer transition-all duration-300 hover:bg-primary hover:-translate-y-0.5"
                title={sidebarOpen ? hideSummaryLabel : showSummaryLabel}
              >
                {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
                <span>{summaryLabel}</span>
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Main Content - Layout avec sommaire à gauche et contenu à droite */}
      <div className="grid grid-cols-1 md:grid-cols-12 w-full min-w-0 flex-1 gap-0 overflow-x-clip main-content-grid">
        {/* Sidebar Desktop - visible uniquement sur md+ */}
        {sidebarOpen && (
          <div
            id="book-table-of-contents"
            role="navigation"
            aria-label={summaryLabel}
            className={`hidden md:block bg-white border-r border-gray-200 h-[calc(100vh-4rem)] sticky top-16 z-40 sidebar-desktop ${isCollapsed ? 'md:col-span-3 lg:col-span-2' : 'md:col-span-3 lg:col-span-3'}`}
          >
            <Sidebar 
              bookData={bookData} 
              onSelectContent={handleSelectContent}
              selectedItem={selectedItem}
              contentLanguage={contentLanguage}
              onClose={toggleSidebar}
              closeLabel={closeSummaryLabel}
            />
          </div>
        )}
        
        {/* Sidebar Mobile Overlay */}
        {sidebarOpen && (
          <div className="fixed md:hidden inset-0 bg-black bg-opacity-50 z-30 top-[110px]"
            onClick={toggleSidebar}
          >
            <div
              id="book-table-of-contents-mobile"
              role="navigation"
              aria-label={summaryLabel}
              className="fixed left-0 top-[110px] w-64 sm:w-72 bg-white border-r border-gray-200 h-[calc(100vh-110px)] overflow-y-auto z-40"
              onClick={(e) => e.stopPropagation()}
            >
              <Sidebar 
                bookData={bookData} 
                onSelectContent={handleSelectContent}
                selectedItem={selectedItem}
                contentLanguage={contentLanguage}
                onClose={toggleSidebar}
                closeLabel={closeSummaryLabel}
              />
            </div>
          </div>
        )}
        
        {/* Content Area - contenu à droite qui défile */}
        <div data-testid="book-content-column" data-book-scroll-container className={`col-span-1 ${sidebarOpen ? (isCollapsed ? 'md:col-span-9 lg:col-span-10' : 'md:col-span-9 lg:col-span-9') : 'md:col-span-12'} w-full min-w-0 overflow-x-hidden overflow-y-auto`}>
          {/* Image de couverture et en-tête dans le contenu principal */}
          {bookData?.book && (() => {
            let coverImageUrl = null;
            if (bookData.book.cover_image) {
             try {
      const href = bookData.book.cover_image;

      // Si le backend renvoie déjà une URL absolue (http/https), on l'utilise telle quelle
      if (href.startsWith('http://') || href.startsWith('https://')) {
        coverImageUrl = href;
      } else {
        // Sinon, on ajoute juste le bon préfixe selon l'environnement
        const isDev =
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';

        const baseUrl = isDev
          ? 'http://localhost:3000'
          : 'https://digitalbook.digetit.com';

        const normalizedPath = href.startsWith('/') ? href : `/${href}`;
        coverImageUrl = `${baseUrl}${normalizedPath}`;
        console.log("coverurl",coverImageUrl)
      }
    } catch (_) {
      // En cas de problème, on retombe sur la valeur brute renvoyée par le backend
      coverImageUrl = bookData.book.cover_image;
    }
            }

            return (
              <div className="w-full bg-white">
                {/* En-tête avec titre et informations - Caché en mobile */}
                <div className="hidden sm:block max-w-4xl mx-auto p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col md:flex-row gap-4 sm:gap-6 items-start">
                    <div className="flex-1">
                      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">{bookData.book.title}</h1>
                      <div className="text-sm sm:text-base text-gray-600 space-y-1 sm:space-y-2">
                        <p>Créé le {new Date(bookData.book.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}</p>
                        <p>{bookData.chapters?.length || 0} chapitre{bookData.chapters?.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image de couverture pleine largeur comme un PDF */}
                {coverImageUrl && (
                  <div className="w-full bg-gray-50 py-0 sm:py-8">
                    <div className="max-w-4xl mx-auto px-3 sm:px-6">
                      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                         <img
                           id="cover-image"
                           src={coverImageUrl}
                           alt={`Couverture de ${bookData.book.title}`}
                           className="w-full h-auto object-contain"
                           style={{ cursor: 'zoom-in' }}
                           onClick={() => {
                             if (!coverImageUrl) return;
                             const caption = `Couverture de ${bookData.book.title}`;
                             setLightbox({ open: true, src: coverImageUrl, alt: caption, caption });
                           }}
                           onError={(e) => {
                             console.log('DocumentViewer - Cover image failed to load:', coverImageUrl);
                             e.target.style.display = 'none';
                             e.target.nextSibling.style.display = 'flex';
                           }}
                           onLoad={() => {
                             console.log('DocumentViewer - Cover image loaded successfully:', coverImageUrl);
                           }}
                         />
                         <div
                           className="w-full h-96 bg-primary flex items-center justify-center text-white"
                           style={{ display: 'none' }}
                         >
                           <FileText size={64} />
                         </div>

                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Contenu du livre (chapitres) */}
          {viewMode === 'full' ? (
            <FullBookContent
              bookData={bookData}
              selectedItem={selectedItem}
              navigationRequestId={navigationRequestId}
              isAdmin={isAdmin}
              canEditStructure={isAdmin || isManager}
              onRegenerateChapter={regenerateQCMForChapter}
              regenLoading={regenLoading}
              onBookContentChanged={reloadBookData}
              contentLanguage={contentLanguage}
              selectedLang={selectedLang}
            />
          ) : selectedItem ? (
            <ContentDisplay
              selectedItem={selectedItem}
              bookData={bookData}
              contentLanguage={contentLanguage}
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <BookOpen size={48} className="text-gray-400 mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">{welcomeTitle}</h2>
              <p className="text-gray-600 mb-4">
                {welcomeText}
              </p>
              <p className="text-sm text-gray-500">
                {welcomeHint} <FileText size={16} />
              </p>
            </div>
          )}
          {translateMessage && (
            <div className="px-4 py-2 text-xs sm:text-sm text-blue-800 bg-blue-50 border border-blue-200 mt-2 rounded max-w-4xl mx-auto">
              {translateMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
